import React from 'react';
import {
  ButtonGrid,
  ButtonTemplate,
  ListTemplate,
  QuickReplies,
  DynamicForm,
  Attachments,
} from '@omilia/chat-ui';
import { DateTime } from 'components/DateTime';
import { RICH_CONTENT_V2 } from 'constants/chat';
import { useDispatch, useSelector } from 'react-redux';
import Form from 'components/Form';
import { getAttachmentsConfig } from 'store/services/chat/selectors';
import { setAttachmentsConfig } from 'store/services/chat/actions';
import { getLocale } from 'store/services/session/selectors';

interface RichContentProps {
  message: {
    type:
      | 'BUTTONS'
      | 'BUTTON_GRID'
      | 'INLINE_BUTTONS'
      | 'VERTICAL_LIST'
      | 'CALENDAR'
      | 'FORM'
      | 'ATTACHMENT'
      | 'RICH_CONTENT_V2';
    title?: string;
    subtitle?: string;
    image_url?: string;
    entries?: Array<any>;
    options: Array<any>;
  } & Record<string, any>;
  onSelect: (object) => void;
  uploadFile: () => void;
  triggerScroll: () => void;
}

const RichContent: React.FC<RichContentProps> = ({
  message,
  onSelect,
  uploadFile,
  triggerScroll,
}) => {
  const dispatch = useDispatch();
  const attachmentsConfig = useSelector(getAttachmentsConfig);
  const locale = useSelector(getLocale);

  const sendRichContentV2 = (messageId: string, fields: any) => {
    onSelect({
      rich_content: {
        id: messageId,
        fields,
      },
    });
  };

  const enableAttachments = () =>
    dispatch(
      setAttachmentsConfig({
        attachmentsRichEnabled: true,
      }),
    );

  if (message.type === 'BUTTON_GRID') {
    return (
      <ButtonGrid
        replies={message.options}
        onSelect={({ label: utterance, context: semantics }) =>
          onSelect({ utterance, semantics })
        }
      />
    );
  }

  if (message.type === 'INLINE_BUTTONS') {
    return (
      <ButtonTemplate
        title={message.title}
        replies={message.options}
        onSelect={({ label: utterance, context: semantics }) =>
          onSelect({ utterance, semantics })
        }
      />
    );
  }

  if (message.type === 'VERTICAL_LIST') {
    return (
      <ListTemplate
        title={message.title}
        items={message.entries}
        onSelect={({ label: utterance, context: semantics }) =>
          onSelect({ utterance, semantics })
        }
      />
    );
  }

  if (message.type === 'FORM') {
    return (
      <DynamicForm
        title={message.title}
        subtitle={message.subtitle}
        fields={message.input_fields}
        onSubmit={formValues =>
          onSelect({
            utterance: 'Submit',
            semantics: formValues,
          })
        }
      />
    );
  }

  if (message.type === 'ATTACHMENT') {
    return (
      <Attachments
        isDisabled={!attachmentsConfig.attachmentsRichEnabled}
        allowedTypes={message?.types_allowed || []}
        maxSize={message?.max_size}
        maxFiles={message?.max_attachments}
        onCancel={() =>
          onSelect({
            utterance: message?.cancel_button_label,
          })
        }
        onSkip={() =>
          onSelect({
            utterance: message?.skip_button_label,
          })
        }
        onUpload={enableAttachments}
        onFileOpen={uploadFile}
        uploadButtonText={message?.submit_button_label}
        cancelButtonText={message?.cancel_button_label}
        skipButtonText={message?.skip_button_label}
      />
    );
  }

  if (message.type === RICH_CONTENT_V2 && message.content.type === 'BUTTONS') {
    return (
      <QuickReplies
        layout={message.content.layout}
        replies={message.content.options}
        isFullWidth={message.content?.full_width}
        onSelect={({ id }) => sendRichContentV2(message.id, { [id]: true })}
      />
    );
  }

  if (message.type === RICH_CONTENT_V2 && message.content.type === 'DATETIME') {
    return (
      <DateTime
        locale={locale}
        withCalendar={message.content.with_calendar}
        withTime={message.content.with_time}
        validation={{
          availableDates: message.content.validation?.available_dates,
          timeStep: message.content.validation?.time_step,
        }}
        submitButtonLabel={message.content?.submit_button_label}
        triggerScroll={triggerScroll}
        onSubmit={(date: string) => {
          sendRichContentV2(message.id, { [message.content.id]: date });
        }}
      />
    );
  }

  if (message.type === RICH_CONTENT_V2 && message.content.type === 'FORM') {
    return (
      <Form
        label={message.content?.label}
        submitButtonLabel={message.content?.submit_button_label}
        fields={message.content.fields}
        onSubmit={(formFields: any) => {
          sendRichContentV2(message.id, formFields);
        }}
      />
    );
  }

  return null;
};

export default RichContent;
